import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import useReducedMotion from '../../hooks/useReducedMotion';
import './hero.css';

const HERO_VIDEO_SRC = '/media/clip-assembly.mp4';
const HERO_POSTER_SRC = '/media/hero-poster.webp';
// Time-based smoothing rate (per second), frame-rate independent.
const SCRUB_RATE = 9;
const MIN_PROGRESS_DELTA = 0.0008;
// The clip finishes assembling at this scroll progress, then dissolves into
// the live DOM scene beneath it (which shares the exact same composition).
const ASSEMBLY_END = 0.55;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  el.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
};

/*
 * Floating command-center scene built from real product screenshots.
 * A generated assembly clip (whose final frame is a capture of this very
 * scene) plays on top while scrolling, then fades out to reveal the live
 * panels — so the cinematic motion always lands on genuine, crisp UI.
 */
const PANELS = [
  {
    key: 'main',
    className: 'hx-panel--main',
    url: 'app.phennx.io/home',
    src: '/product/home-dashboard.webp',
    width: 1600,
    height: 1200,
  },
  {
    key: 'monitor',
    className: 'hx-panel--monitor',
    url: 'app.phennx.io/monitor',
    src: '/product/monitor-workspace.webp',
    width: 1600,
    height: 1200,
  },
  {
    key: 'qc',
    className: 'hx-panel--qc',
    url: 'app.phennx.io/qc/instances',
    src: '/product/quality-control.webp',
    width: 1600,
    height: 1200,
  },
];

export default function CinematicHero() {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const [videoFailed, setVideoFailed] = useState(false);
  const [wantsVideo] = useState(
    () =>
      (window.matchMedia?.('(min-width: 961px)')?.matches ?? false) &&
      (window.matchMedia?.('(pointer: fine)')?.matches ?? false)
  );

  const showVideo = wantsVideo && !prefersReducedMotion && !videoFailed;

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const section = sectionRef.current;
    if (!section) return undefined;

    let rafId = null;
    let running = false;
    let target = 0;
    let current = 0;
    let lastTime = null;

    const measure = () => {
      const rect = section.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      target = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
    };

    const tick = (now) => {
      const dt = lastTime === null ? 1 / 60 : Math.min(0.1, (now - lastTime) / 1000);
      lastTime = now;
      current += (target - current) * (1 - Math.exp(-SCRUB_RATE * dt));
      if (Math.abs(target - current) < MIN_PROGRESS_DELTA) {
        current = target;
      }
      section.style.setProperty('--hero-progress', current.toFixed(4));

      const video = videoRef.current;
      if (video && video.readyState >= 2 && Number.isFinite(video.duration)) {
        const clipProgress = Math.min(1, current / ASSEMBLY_END);
        const nextTime = clipProgress * video.duration;
        if (Math.abs(video.currentTime - nextTime) > 0.01) {
          try {
            video.currentTime = nextTime;
          } catch {
            // Seeking can fail before metadata settles; next frame retries.
          }
        }
      }

      rafId = window.requestAnimationFrame(tick);
    };

    const start = () => {
      if (running) return;
      running = true;
      measure();
      rafId = window.requestAnimationFrame(tick);
    };

    const stop = () => {
      running = false;
      lastTime = null;
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const handleScroll = () => measure();

    let observer = null;
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            start();
          } else {
            stop();
          }
        },
        { threshold: 0 }
      );
      observer.observe(section);
    } else {
      start();
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      stop();
      observer?.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [prefersReducedMotion]);

  return (
    <section ref={sectionRef} className="hx-hero" aria-labelledby="hero-heading">
      <div className={`hx-hero__stage${showVideo ? ' hx-hero__stage--video' : ''}`}>
        <div className="hx-hero__glow" aria-hidden="true" />

        <div className="hx-hero__scene" aria-hidden="true">
          <div className="hx-hero__cluster">
          {PANELS.map((panel) => (
            <div key={panel.key} className={`hx-hero__float hx-hero__float--${panel.key}`}>
              <div className="hx-hero__drift">
                <figure className={`hx-panel ${panel.className}`}>
                  <div className="hx-panel__bar">
                    <span className="hx-panel__dot" />
                    <span className="hx-panel__dot" />
                    <span className="hx-panel__dot" />
                    <span className="hx-panel__url">{panel.url}</span>
                  </div>
                  <img
                    src={panel.src}
                    alt=""
                    width={panel.width}
                    height={panel.height}
                    loading="eager"
                    fetchpriority={panel.key === 'main' ? 'high' : 'auto'}
                    draggable={false}
                  />
                </figure>
              </div>
            </div>
          ))}

          <div className="hx-hero__float hx-hero__float--chip-audit">
            <div className="hx-hero__drift">
              <div className="hx-chip">
                <span className="hx-chip__dot hx-chip__dot--signal" />
                <div>
                  <p className="hx-chip__title">Document signed</p>
                  <p className="hx-chip__meta">Main ICF v3.1 · audit trail updated</p>
                </div>
              </div>
            </div>
          </div>

          <div className="hx-hero__float hx-hero__float--chip-task">
            <div className="hx-hero__drift">
              <div className="hx-chip">
                <i className="pi pi-check-circle hx-chip__icon" aria-hidden="true" />
                <div>
                  <p className="hx-chip__title">QC check completed</p>
                  <p className="hx-chip__meta">IP accountability · CARD-HTN-301</p>
                </div>
              </div>
            </div>
          </div>

          <div className="hx-hero__float hx-hero__float--chip-ready">
            <div className="hx-hero__drift">
              <div className="hx-chip hx-chip--wide">
                <div className="hx-chip__stack">
                  <p className="hx-chip__title">Document readiness</p>
                  <span className="hx-chip__track">
                    <span className="hx-chip__fill" />
                  </span>
                </div>
                <span className="hx-chip__value">82%</span>
              </div>
            </div>
          </div>
          </div>
        </div>

        {showVideo ? (
          <video
            ref={videoRef}
            className="hx-hero__video"
            src={HERO_VIDEO_SRC}
            poster={HERO_POSTER_SRC}
            muted
            playsInline
            preload="auto"
            tabIndex={-1}
            aria-hidden="true"
            onError={() => setVideoFailed(true)}
          />
        ) : null}

        <div className="hx-hero__scrim" aria-hidden="true" />

        <div className="hx-hero__content">
          <p className="hx-eyebrow">
            <span className="hx-eyebrow__tick" aria-hidden="true" />
            Clinical trial management, rebuilt
          </p>
          <h1 className="hx-hero__title" id="hero-heading">
            Clinical research operations,
            <br />
            <span className="hx-hero__title-accent">in one command center.</span>
          </h1>
          <p className="hx-hero__sub">
            Phennx helps research teams coordinate studies, visits, documents, monitoring, payments,
            and audit-ready workflows — with less operational drift.
          </p>

          <div className="hx-hero__actions">
            <Button
              label="Request a demo"
              icon="pi pi-send"
              className="hx-btn hx-btn--primary"
              onClick={() => scrollToId('contact')}
            />
            <Button
              label="Explore platform"
              icon="pi pi-compass"
              className="hx-btn hx-btn--ghost"
              onClick={() => scrollToId('platform')}
            />
          </div>

          <p className="hx-hero__note">
            <i className="pi pi-circle-fill" aria-hidden="true" />
            Early access — enrolling new sites soon
          </p>
        </div>

        <div className="hx-hero__cue" aria-hidden="true">
          <span className="hx-hero__cue-line" />
          <span className="hx-hero__cue-label">Scroll</span>
        </div>
      </div>
    </section>
  );
}
